import type { NavBadgeTone } from '@nexvia-logistics/ui';

/**
 * `NavBadgeTone` is the design system's own type (`primary`: a number to act on eventually;
 * `critical`: something is wrong). It used to be mirrored here while the package was not a
 * dependency; it is now (NEX-1164, 2026-10-15), so it is imported instead.
 *
 * `NavItem` below stays local on purpose: this is the router-facing list (`activeNavIdFor`,
 * `DEFAULT_ROUTE`) and carries no JSX, while the package's own `NavItem` requires an `icon`.
 * `AppLayout.tsx` builds the real ones from these, adding the icon and the live badge count.
 */
export interface NavItem {
  id: string;
  label: string;
  href: string;
  /**
   * A count the package `AppShell` renders as a trailing pill (falsy — `0`/`undefined` — renders
   * nothing). Never set in `NAV_ITEMS` below:
   * these 5 rows are the static id/label/href every route always has, not live data. The real,
   * per-render counts (Solicitudes/Mis cotizaciones/Mis depósitos) come from `useNavCounts.ts` and
   * are merged onto these items by `AppLayout.tsx` at render time.
   */
  badge?: number;
  /** Defaults to `'primary'` in the package `AppShell` — pass `'critical'` only when the count itself means something is wrong, not just "pending". */
  badgeTone?: NavBadgeTone;
}

/**
 * Same 5 destinations and order as the design export's DP_NAV (Deposito/dep-shell.jsx,
 * content/flow reference only — see docs/superpowers/plans/2026-10-13-storage/roadmap.md §4 in
 * nexvia-api-web). "Solicitudes" first and default: matches the export's initial route.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'solicitudes', label: 'Solicitudes', href: '/solicitudes' },
  { id: 'cotizaciones', label: 'Mis cotizaciones', href: '/cotizaciones' },
  { id: 'depositos', label: 'Mis depósitos', href: '/depositos' },
  { id: 'servicios', label: 'Servicios y condiciones', href: '/servicios' },
  { id: 'empresa', label: 'Mi empresa', href: '/empresa' }
];

export const DEFAULT_ROUTE = NAV_ITEMS[0].href;

/** Which sidebar entry should read as current for a given pathname (nested paths included). */
export const activeNavIdFor = (pathname: string): string =>
  NAV_ITEMS.find((item) => pathname === item.href || pathname.startsWith(`${item.href}/`))?.id ?? NAV_ITEMS[0].id;
